import React from 'react';
import { Alert, AlertTitle, Typography, Box } from '@mui/material';
import { AuthError, AuthErrorCategory } from '../../types/auth/AuthError';

interface ErrorMessageProps {
  error: AuthError;
  showDetails?: boolean;
  variant?: 'standard' | 'filled' | 'outlined';
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  error,
  showDetails = false,
  variant = 'standard'
}) => {
  const getUKEnglishMessage = (error: AuthError): { title: string; message: string; guidance?: string } => {
    switch (error.category) {
      case AuthErrorCategory.NETWORK:
        return {
          title: 'Connection Issue',
          message: 'We\'re having trouble connecting to our servers.',
          guidance: 'Please check your internet connection and try again. If the problem persists, please contact support.'
        };

      case AuthErrorCategory.AUTH:
        if (error.code === 'NotAuthorizedException') {
          return {
            title: 'Sign In Failed',
            message: 'The email address or password you entered is incorrect.',
            guidance: 'Please check your credentials and try again. If you\'ve forgotten your password, use the "Forgot Password" link.'
          };
        }
        if (error.code === 'UserNotConfirmedException') {
          return {
            title: 'Account Not Verified',
            message: 'Your account hasn\'t been verified yet.',
            guidance: 'Please check your email for a verification link, or request a new verification email.'
          };
        }
        if (error.code === 'UserNotFoundException') {
          return {
            title: 'Account Not Found',
            message: 'We couldn\'t find an account with that email address.',
            guidance: 'Please check the email address or create a new account if you haven\'t registered yet.'
          };
        }
        if (error.code === 'TooManyRequestsException') {
          return {
            title: 'Too Many Attempts',
            message: 'You\'ve made too many sign-in attempts.',
            guidance: 'Please wait a few minutes before trying again, or reset your password if you\'ve forgotten it.'
          };
        }
        return {
          title: 'Authentication Error',
          message: 'There was a problem signing you in.',
          guidance: 'Please try again. If the issue continues, please contact our support team.'
        };

      case AuthErrorCategory.VALIDATION:
        if (error.field === 'email') {
          return {
            title: 'Invalid Email Address',
            message: 'Please enter a valid email address.',
            guidance: 'Email addresses should include an @ symbol and a domain (e.g., example@university.edu).'
          };
        }
        if (error.field === 'password') {
          return {
            title: 'Invalid Password',
            message: 'Your password doesn\'t meet the requirements.',
            guidance: 'Passwords must be at least 8 characters long and include uppercase letters, lowercase letters, numbers, and special characters.'
          };
        }
        if (error.field === 'confirmationCode') {
          return {
            title: 'Invalid Verification Code',
            message: 'The verification code you entered is incorrect.',
            guidance: 'Please check the code in your email and try again, or request a new verification code.'
          };
        }
        return {
          title: 'Input Error',
          message: 'Please check your input and try again.',
          guidance: 'Make sure all required fields are filled in correctly.'
        };

      case AuthErrorCategory.SYSTEM:
        return {
          title: 'Technical Difficulty',
          message: 'We\'re experiencing technical difficulties.',
          guidance: 'Our team has been notified. Please try again in a few minutes. If the problem persists, please contact support.'
        };

      default:
        return {
          title: 'Unexpected Error',
          message: 'Something unexpected happened.',
          guidance: 'Please try again. If the problem continues, please contact our support team.'
        };
    }
  };

  const getSeverityLevel = (error: AuthError) => {
    switch (error.severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'warning';
    }
  };

  const messageContent = getUKEnglishMessage(error);

  return (
    <Alert severity={getSeverityLevel(error) as any} variant={variant}>
      <AlertTitle>{messageContent.title}</AlertTitle>
      <Typography variant="body2" paragraph>
        {messageContent.message}
      </Typography>

      {messageContent.guidance && (
        <Typography variant="body2" color="text.secondary">
          {messageContent.guidance}
        </Typography>
      )}

      {showDetails && process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Technical Details:</strong><br />
            Code: {error.code || 'N/A'}<br />
            Operation: {error.operation}<br />
            Timestamp: {error.timestamp.toLocaleString('en-GB')}<br />
            Category: {error.category}
          </Typography>
        </Box>
      )}
    </Alert>
  );
};

// Pre-defined UK English error messages for common scenarios
export const UKErrorMessages = {
  NETWORK_TIMEOUT: 'We\'re having trouble reaching our servers. Please check your connection and try again.',
  INVALID_CREDENTIALS: 'The email address or password you entered is incorrect. Please try again.',
  ACCOUNT_LOCKED: 'Your account has been temporarily locked for security reasons. Please try again later or contact support.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again to continue.',
  WEAK_PASSWORD: 'Your password isn\'t strong enough. Please use at least 8 characters including uppercase letters, lowercase letters, numbers, and symbols.',
  EMAIL_ALREADY_EXISTS: 'An account with this email address already exists. Please sign in or use a different email address.',
  VERIFICATION_REQUIRED: 'Please check your email and click the verification link to activate your account.',
  SERVER_MAINTENANCE: 'Our systems are temporarily unavailable for maintenance. Please try again shortly.',
  UNSUPPORTED_BROWSER: 'Your browser version isn\'t supported. Please update your browser or try a different one.',
  NETWORK_OFFLINE: 'You appear to be offline. Please check your internet connection and try again.'
} as const;